import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { ShieldCheck, Upload, Loader2, Phone } from "lucide-react";

export const SterilizationForm = ({
  isRTL,
  formData,
  setFormData,
  instrumentsCatalog,
  selectedInstruments,
  setSelectedInstruments,
  toggleInstrument,
  activeCategory,
  setActiveCategory,
  boxPhoto,
  handleBoxPhotoUpload,
  sterileNotes,
  setSterileNotes,
  submitting,
  handleSterilizationSubmit,
  onBack,
  t,
}) => (
  <Card className="border-0 shadow-sm" data-testid="sterilization-form-card">
    <CardHeader>
      <div className="flex items-center justify-between">
        <CardTitle className="text-lg text-emerald-700 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5" />
          {isRTL ? "تفاصيل طلب التعقيم" : "Sterilization Request"}
        </CardTitle>
        <Button variant="ghost" size="sm" onClick={onBack} className="text-slate-500">
          {isRTL ? "← رجوع" : "← Back"}
        </Button>
      </div>
    </CardHeader>
    <CardContent>
      <form onSubmit={handleSterilizationSubmit} className="space-y-5">
        {/* Reporter Info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="block">{isRTL ? "الاسم" : "Name"} *</Label>
            <Input
              value={formData.reporter_name}
              onChange={(e) => setFormData({ ...formData, reporter_name: e.target.value })}
              placeholder={isRTL ? "اسمك" : "Your name"}
              className={isRTL ? "text-right" : "text-left"}
              required
              data-testid="sterile-reporter-name"
            />
          </div>
          <div className="space-y-2">
            <Label className="block">{isRTL ? "رقم واتساب" : "WhatsApp"} *</Label>
            <div className="flex gap-2 items-center" dir="ltr">
              <div className="flex items-center gap-1 bg-slate-100 border border-slate-200 rounded-md px-3 h-10 text-sm font-medium text-slate-700 shrink-0">
                <Phone className="w-3.5 h-3.5 text-green-500" />
                <span>966+</span>
              </div>
              <Input
                value={formData.reporter_whatsapp}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  const cleaned = val.startsWith("0") ? val.slice(1) : val;
                  if (cleaned.length <= 9) setFormData({ ...formData, reporter_whatsapp: cleaned });
                }}
                placeholder="5XXXXXXXX"
                className="text-left"
                dir="ltr"
                required
                data-testid="sterile-reporter-whatsapp"
              />
            </div>
          </div>
        </div>

        {/* Instruments — 2-step: Category → Items */}
        <div className="space-y-3">
          <Label className="block">
            {isRTL ? "الأدوات" : "Instruments"} <span className="text-red-500">*</span>
          </Label>

          {instrumentsCatalog.length === 0 ? (
            <div className="text-sm text-slate-400 border rounded-md p-3">
              {isRTL ? "جاري تحميل قائمة الأدوات..." : "Loading instruments..."}
            </div>
          ) : (
            <>
              {/* Category chips */}
              <div className="grid grid-cols-2 gap-2">
                {[...new Set(instrumentsCatalog.map((i) => i.category || "Other"))].map((cat) => {
                  const total = instrumentsCatalog.filter(
                    (i) => (i.category || "Other") === cat
                  ).length;
                  const picked = instrumentsCatalog.filter(
                    (i) => (i.category || "Other") === cat && selectedInstruments[i.id]
                  ).length;
                  const isActive = activeCategory === cat;
                  return (
                    <button
                      type="button"
                      key={cat}
                      onClick={() => setActiveCategory(isActive ? null : cat)}
                      className={`relative border rounded-xl p-3 text-left transition-all ${
                        isActive
                          ? "border-emerald-600 bg-emerald-50 shadow-sm"
                          : "border-slate-200 bg-white hover:border-emerald-300"
                      }`}
                      data-testid={`cat-btn-${cat.replace(/\s+/g, "-")}`}
                    >
                      <p className="text-sm font-semibold text-slate-800">{cat}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{total} items</p>
                      {picked > 0 && (
                        <span className="absolute top-2 right-2 bg-emerald-600 text-white text-xs font-bold rounded-full min-w-[22px] h-[22px] flex items-center justify-center px-1.5">
                          {picked}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Items of active category */}
              {activeCategory && (
                <div className="border rounded-md p-3 bg-slate-50 space-y-1.5 max-h-72 overflow-y-auto">
                  <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">{activeCategory}</p>
                  {instrumentsCatalog
                    .filter((i) => (i.category || "Other") === activeCategory)
                    .map((inst) => (
                      <label
                        key={inst.id}
                        className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors ${
                          selectedInstruments[inst.id]
                            ? "bg-emerald-100 border border-emerald-300"
                            : "bg-white border border-slate-200 hover:border-emerald-300"
                        }`}
                        data-testid={`instr-check-${inst.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={!!selectedInstruments[inst.id]}
                          onChange={() => toggleInstrument(inst.id)}
                          className="w-4 h-4 accent-emerald-600"
                        />
                        <p className="text-sm text-slate-800 flex-1">{inst.name_en}</p>
                      </label>
                    ))}
                </div>
              )}
            </>
          )}

          <div className="flex items-center justify-between text-xs">
            <p className="text-slate-500">
              {isRTL
                ? `تم اختيار ${Object.keys(selectedInstruments).length} أداة`
                : `${Object.keys(selectedInstruments).length} instrument(s) selected`}
            </p>
            {Object.keys(selectedInstruments).length > 0 && (
              <button
                type="button"
                onClick={() => setSelectedInstruments({})}
                className="text-red-500 hover:underline"
                data-testid="clear-instruments-btn"
              >
                {isRTL ? "مسح الكل" : "Clear all"}
              </button>
            )}
          </div>
        </div>

        {/* Box Photo */}
        <div className="space-y-2">
          <Label className="block">
            {isRTL ? "صورة الصندوق" : "Box Photo"} <span className="text-red-500">*</span>
          </Label>
          <div
            className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
              !boxPhoto ? "border-red-300 bg-red-50" : "border-emerald-300 bg-emerald-50"
            }`}
          >
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleBoxPhotoUpload}
              className="hidden"
              id="box-photo-upload"
              data-testid="box-photo-input"
            />
            <label htmlFor="box-photo-upload" className="cursor-pointer">
              {boxPhoto ? (
                <div className="space-y-2">
                  <img
                    src={boxPhoto}
                    alt="Box"
                    className="w-32 h-32 object-cover rounded-lg mx-auto border"
                  />
                  <p className="text-sm text-emerald-700">
                    {isRTL ? "اضغط لتغيير الصورة" : "Click to replace photo"}
                  </p>
                </div>
              ) : (
                <>
                  <Upload className="w-10 h-10 mx-auto mb-2 text-red-400" />
                  <p className="text-slate-600">
                    {isRTL ? "اضغط لالتقاط/رفع صورة الصندوق" : "Tap to capture/upload the box photo"}
                  </p>
                  <p className="text-xs text-red-500 font-medium">{isRTL ? "مطلوب" : "Required"}</p>
                </>
              )}
            </label>
          </div>
        </div>

        {/* Notes */}
        <div className="space-y-2">
          <Label className="block">{isRTL ? "ملاحظات (اختياري)" : "Notes (optional)"}</Label>
          <Textarea
            value={sterileNotes}
            onChange={(e) => setSterileNotes(e.target.value)}
            placeholder={isRTL ? "أي ملاحظات..." : "Any notes..."}
            className={`min-h-[80px] ${isRTL ? "text-right" : "text-left"}`}
            data-testid="sterile-notes"
          />
        </div>

        <Button
          type="submit"
          className="w-full bg-emerald-600 hover:bg-emerald-700 py-6 text-lg text-white"
          disabled={submitting}
          data-testid="sterile-submit-btn"
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin mx-2 text-white" />
              <span className="text-white">{isRTL ? "جاري الإرسال..." : "Submitting..."}</span>
            </>
          ) : (
            <>
              <ShieldCheck className="w-5 h-5 mx-2 text-white" />
              <span className="text-white">
                {isRTL ? "إرسال طلب التعقيم" : "Submit Sterilization Request"}
              </span>
            </>
          )}
        </Button>
      </form>
    </CardContent>
  </Card>
);
